<template>

  <m-pay-layout>
    <v-layout justify-space-around :column="$vuetify.breakpoint.smAndDown">

      <m-pay-confirm-dialog
        :show="showConfirmationDialog"
        :title="confirmationDialog.description"
        @yes="confirmationDialog.action"
        @cancel="hideDialog"
        maxWidth="700px"
      />

      <v-flex>
        <v-card class="mb-3 mx-2">
          <v-card-title>
            <span class="headline">Update Widget</span>
          </v-card-title>

          <m-pay-widget-editor
            :widget="widget"
            :loading="loading"
            @submit="updateWidgetWithConfirmation"
            @cancel="cancel"
          />
        </v-card>

        <v-card class="mb-3 mx-2">
          <v-card-text>
            <div>
              <div class="title">
                {{ widget.id }}
              </div>
              <br/>
              <div>Created at
                <span class="body-2">
                  {{ formatters.dateTime(widget.createdDate) }}
                </span>
                by
                <v-chip small color="primary" text-color="white">
                  <v-avatar>
                    <v-icon>account_circle</v-icon>
                  </v-avatar>
                  {{ widget.createdBy }}
                </v-chip>
              </div>
              <div>Updated at
                <span class="body-2">
                  {{ formatters.dateTime(widget.updatedDate) }}
                </span>
                by
                <v-chip small color="primary" text-color="white">
                  <v-avatar>
                    <v-icon>account_circle</v-icon>
                  </v-avatar>
                  {{ widget.updatedBy }}
                </v-chip>
              </div>
              <div>Status
                <v-chip small :color="widgetStatusClasses[widget.status]" text-color="white">
                  <v-avatar>
                    <v-icon>check_circle</v-icon>
                  </v-avatar>
                  {{ widget.status }}
                </v-chip>
              </div>
              <div>Creation method
                <v-chip small color="primary" text-color="white">
                  {{ widget.creationMethod }}
                </v-chip>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="mb-3 mx-2">
          <v-card-title>
            <span class="headline">Actions</span>
          </v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="mx-2" :loading="loading"
                   :disabled="loading"
                   @click.prevent="cloneWidget">
              Clone widget
              <v-icon right dark>toggle_on</v-icon>
            </v-btn>
            <v-btn v-if="widget.status === 'ACTIVE'" large flat color="secondary"
                   class="mx-2" :loading="loading"
                   :disabled="loading"
                   @click.prevent="disableWidget">
              Disable widget
              <v-icon right dark>toggle_off</v-icon>
            </v-btn>
            <v-btn v-if="widget.status === 'DISABLED'" large flat color="secondary"
                   class="mx-2" :loading="loading"
                   :disabled="loading"
                   @click.prevent="activateWidget">
              Activate widget
              <v-icon right dark>toggle_off</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
        </v-flex>

        <v-flex v-if="widget.type === 'INSTANT'">
          <v-card class="mb-3 mx-2">
            <v-card-title>
              <span class="headline">Order Items</span>
            </v-card-title>
            <v-card-text>
              <m-pay-widget-items-table
                :widgetItems="widgetItems"
                :widget="widget"
                :allowAddItems="allowAddItems"
                :loading="loading"
                @saveItem="updateWidgetItem"
                @deleteItem="updateWidgetItem">
              </m-pay-widget-items-table>
            </v-card-text>
          </v-card>
        </v-flex>

    </v-layout>
  </m-pay-layout>

</template>

<script>
import axios from 'axios';
import queries from '../../utils/queries';
import MPayWidgetEditor from './MPayWidgetEditor';
import MPayConfirmDialog from '../MPayConfirmDialog';
import { widgetStatusClasses } from '../../utils/statuses';
import formatters from '../../utils/formatters';
import MPayWidgetItemsTable from './MPayWidgetItemsTable';

export default {
  name: 'MPayWidgetUpdateView',

  components: {
    MPayWidgetEditor,
    MPayConfirmDialog,
    MPayWidgetItemsTable,
  },

  data: () => ({
    widget: {
      type: 'INSTANT',
      config: { items: [] },
    },
    widgetItems: [],
    loading: false,
    showConfirmationDialog: false,
    confirmationDialog: {
      action: () => {
      },
      title: '',
      description: '',
    },
    widgetStatusClasses,
    formatters,
    allowAddItems: false,
  }),

  methods: {
    cancel() {
      this.$router.push({ name: 'widgets' });
    },
    fetchWidget() {
      if (this.$route.query.created) {
        this.showSuccessMessage('Widget successfully created');
      } else {
        this.loading = true;
        axios.get(queries.merchantWidget(this.$route.params.widgetId))
          .then((response) => {
            this.widget = response.data;
            this.widgetItems = this.widget.config.items;
            this.loading = false;
            this.allowAddItems = this.widget.id.length > 0 && this.widget.type === 'INSTANT';
          })
          .catch((e) => {
            this.showError(e);
            this.loading = false;
          });
      }
    },
    updateWidgetWithConfirmation(widget) {
      if (widget.type === 'CHECKOUT' && widget.config.items.length > 0) {
        this.showDialog('Change to type "CHECKOUT" will delete all configured widget items. Proceed?', () => {
          this.hideDialog();
          this.updateWidget(widget);
        });
      } else {
        this.updateWidget(widget);
      }
    },
    updateWidget(widget) {
      this.loading = true;
      /* eslint-disable no-param-reassign */
      axios.put(queries.merchantWidget(this.widget.id), widget)
        .then(() => {
          this.loading = false;
          this.showSuccessMessage('Widget successfully updated.');
          this.fetchWidget();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    updateWidgetItem(items) {
      this.widget.config.items = items;
      this.updateWidget(this.widget);
    },
    cloneWidget() {
      this.$router.push({ name: 'add-widget',
        query: { id: this.widget.id,
          type: this.widget.type } });
    },
    disableWidget() {
      this.loading = true;
      /* eslint-disable no-param-reassign */
      axios.put(queries.disableMerchantWidget(this.widget.id))
        .then(() => {
          this.loading = false;
          this.showSuccessMessage('Widget successfully disabled.');
          this.fetchWidget();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    activateWidget() {
      this.loading = true;
      /* eslint-disable no-param-reassign */
      axios.put(queries.activateMerchantWidget(this.widget.id))
        .then(() => {
          this.loading = false;
          this.showSuccessMessage('Widget successfully activated.');
          this.fetchWidget();
        })
        .catch((e) => {
          this.showError(e);
          this.loading = false;
        });
    },
    showDialog(message, onSuccessCallback) {
      this.showConfirmationDialog = true;
      this.confirmationDialog = {
        title: '',
        description: message,
        action: onSuccessCallback,
      };
    },
    hideDialog() {
      this.showConfirmationDialog = false;
    },
  },

  watch: {
    $route() {
    },
  },

  mounted() {
    this.fetchWidget();
  },

};
</script>



// WEBPACK FOOTER //
// src/components/merchant/MPayWidgetUpdateView.vue